import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Form, Row, Col, Button } from "react-bootstrap"
import Select from "react-select"
import { useTranslation } from "react-i18next"
import { getForSelect as getAnimalsForSelect } from '../../../actions/other/animalsActions'
import { getForSelect as getLotsForSelect } from '../../../actions/other/lotsActions'

import { getBreedOptions } from "../../../constants/breedOptions"
import { getSexOptions } from "../../../constants/sexOptions"
import { getBooleanOptions } from "../../../constants/booleanOptions"
import { getReproductiveStatusOptions } from "../../../constants/reproductiveStatusOptions"
import { getCategoryOptions } from "../../../constants/categoryOptions"

export default function EventFormAlta({ type, state, handleInputChange, submit, isRegistering, isAddingOrEditing }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const { animalsForSelect } = useSelector(state => state.animals)
	const { lotsForSelect } = useSelector(state => state.lots)

	useEffect(() => {
		dispatch(getAnimalsForSelect())
		dispatch(getLotsForSelect())
	}, [dispatch])

	const lots = lotsForSelect?.map(lot => ({
		value: lot._id,
		label: lot.identifier
	}))

	const females = animalsForSelect.filter(a => a.sex === 'hembra')
	const males = animalsForSelect.filter(a => a.category === 'semental' || a.sex === 'macho')

    const breedOptions = getBreedOptions(t)
    const sexOptions = getSexOptions(t)
    const booleanOptions = getBooleanOptions(t)
    const reproductiveStatusOptions = getReproductiveStatusOptions(t)
	const categoryOptions = getCategoryOptions(t)

	return (
		<Form onSubmit={submit} className="formulario">
			<Row className="my-2">
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.identifier')}</Form.Label>
						<Form.Control
							type="text"
							name="identifier"
							value={state.identifier || ''}
							onChange={handleInputChange}
							required
						/>
					</Form.Group>
				</Col>
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.crotal')}</Form.Label>
						<Form.Control
							type="text"
							name="crotal"
							value={state.crotal || ''}
							onChange={handleInputChange}
							required
						/>
					</Form.Group>
				</Col>
			</Row>

			<Row className="my-2">
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.birthDate')}</Form.Label>
						<Form.Control
							type="date"
							name="birthDate"
							value={state.birthDate || ''}
							onChange={handleInputChange}
							required
						/>
					</Form.Group>
				</Col>
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.sex')}</Form.Label>
						<Select
							options={sexOptions}
							value={sexOptions.find(o => o.value === state.sex) || null}
							onChange={opt => handleInputChange({ target: { name: 'sex', value: opt.value } })}
						/>
					</Form.Group>
				</Col>
			</Row>

			<Row className="my-3">
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.breed')}</Form.Label>
						<Select
							options={breedOptions}
							value={breedOptions.find(o => o.value === state.breed) || null}
							onChange={opt => handleInputChange({ target: { name: 'breed', value: opt.value } })}
						/>
					</Form.Group>
				</Col>
				<Col md={6}>
					<Form.Group className="mb-3">
						<Form.Label>{t('admin.animal.category')}</Form.Label>
						<Select
							options={categoryOptions}
							value={categoryOptions.find(o => o.value === state.category)}
							onChange={option => {
								if (!['vaca', 'novilla'].includes(option.value)) {
									handleInputChange({ target: { name: 'reproductiveStatus', value: null } })
									handleInputChange({ target: { name: 'lastBirthDate', value: null } })
								}
								handleInputChange({ target: { name: 'category', value: option.value } })
							}}
						/>
					</Form.Group>
				</Col>
				<Col md={6}>
					<Form.Group>
						<Form.Label>{t('admin.animal.lot')}</Form.Label>
						<Select
							options={lots}
							value={lots.find(o => o.value === state.lot) || null}
							onChange={opt => handleInputChange({ target: { name: 'lot', value: opt.value } })}
						/>
					</Form.Group>
				</Col>
			</Row>

			{type === 'birth' && (
				<>
					<Row className="my-2">
						<Col md={6}>
							<Form.Group>
								<Form.Label>{t('admin.animal.mother')}</Form.Label>
								<Select
									options={females?.map(f => ({ value: f._id, label: f.identifier }))}
									value={females?.find(f => f._id === state?.mother) ? { value: state?.mother, label: females?.find(f => f._id === state?.mother)?.identifier } : null}
									onChange={opt => handleInputChange({ target: { name: 'mother', value: opt.value } })}
								/>
							</Form.Group>
						</Col>
						<Col md={6}>
							<Form.Group>
								<Form.Label>{t('admin.animal.father')}</Form.Label>
								<Select
									options={males?.map(m => ({ value: m._id, label: m.identifier }))}
									value={males?.find(f => f._id === state?.father) ? { value: state?.father, label: males?.find(f => f._id === state?.father)?.identifier } : null}
									onChange={opt => handleInputChange({ target: { name: 'father', value: opt.value } })}
								/>
							</Form.Group>
						</Col>
					</Row>
					<Row className="my-2">
						<Col md={6}>
							<Form.Group>
								<Form.Label>{t('admin.animal.weight')} (kg)</Form.Label>
								<Form.Control
									type="number"
									name="weight"
									value={state.weight || ''}
									onChange={handleInputChange}
									min="0"
									step="0.01"
								/>
							</Form.Group>
						</Col>
					</Row>
				</>
			)}

			{type === 'acquisition' && (
				<>
					<Row className="my-2">
						<Col md={6}>
							<Form.Group>
								<Form.Label>{t('admin.animal.castrated')}</Form.Label>
								<Select
									options={booleanOptions}
									value={booleanOptions.find(o => o.value === state.castrated) || null}
									onChange={opt => handleInputChange({ target: { name: 'castrated', value: opt.value } })}
								/>
							</Form.Group>
						</Col>
						<Col md={6}>
							<Form.Group>
								<Form.Label>{t('admin.animal.origin')}</Form.Label>
								<Form.Control
									type="text"
									name="origin"
									value={state.origin || ''}
									onChange={handleInputChange}
								/>
							</Form.Group>
						</Col>
					</Row>
					{state?.category && (state?.category === 'vaca' || state?.category === 'novilla') && (
						<Row className="my-2">
							<Col md={6}>
								<Form.Group>
									<Form.Label>{t('admin.animal.reproductiveStatus')}</Form.Label>
									<Select
										options={reproductiveStatusOptions}
										value={reproductiveStatusOptions.find(o => o.value === state.reproductiveStatus) || null}
										onChange={opt => handleInputChange({ target: { name: 'reproductiveStatus', value: opt.value } })}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group>
									<Form.Label>{t('admin.animal.last_birth_date')}</Form.Label>
									<Form.Control
										type="date"
										name="lastBirthDate"
										value={state.lastBirthDate || ''}
										onChange={handleInputChange}
									/>
								</Form.Group>
							</Col>
						</Row>
					)}
					<Row>
						<Col>
							<Form.Group>
								<Form.Label>{t('admin.animal.additional_data')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={2}
									name="additionalData"
									value={state.additionalData || ''}
									onChange={handleInputChange}
								/>
							</Form.Group>
						</Col>
					</Row>
				</>
			)}

			<Row>
				<Col>
					<Button type="submit" className="w-100" variant="primary">
						{isRegistering
							? <span className="spinner-border spinner-border-sm" />
							: isAddingOrEditing === 'editing'
								? t('admin.animal.edit')
								: t('admin.animal.register')
						}
					</Button>
				</Col>
			</Row>
		</Form>
	)
}
